/**
 * Reporting of errors that happen in the browser to an error tracker.
 *
 * The tracker is GlitchTip (docs/monitoring.md), spoken to with Sentry's SDK. This
 * file is the only one that knows the SDK (ESLint keeps it so): to change the
 * tracker is to change this file.
 *
 * The address comes from the installation when the page loads: /runtime-config.js,
 * which the frontend's container writes at start from APP_FRONTEND_ERROR_REPORTING_DSN
 * (frontend/runtime-config.sh), so a new address needs no new build. On a developer's
 * machine it may come from `VITE_ERROR_REPORTING_DSN` instead. Without an address
 * nothing is loaded and nothing is sent. With one, the SDK is loaded apart
 * from the application, after it has started, so the first screen does not wait
 * for it; what goes wrong in the meantime is kept and sent once it is there.
 *
 * A report carries the error with its stack, the page's route, the build and the
 * account by id and role. It carries no e-mail, nothing typed into a form, and no
 * part of an address after the `?`.
 */
import type { Role } from '@/shared/api/types';

/** The part of the SDK this file uses: what a replacement would have to offer. */
export interface Tracker {
  captureException(error: unknown, context?: { tags?: Record<string, string>; extra?: Record<string, unknown> }): void;
  setUser(user: { id: string; role: string } | null): void;
}

export interface TrackerOptions {
  dsn: string;
  release: string | undefined;
  environment: string;
}

export type LoadTracker = (options: TrackerOptions) => Promise<Tracker>;

export interface ErrorReporter {
  /** Begin loading the tracker. Does nothing without an address. */
  start(): void;
  /** Report something that went wrong. `where` says which part of the interface it was in. */
  report(error: unknown, where?: string): void;
  /** Whose session this is, or null once it has ended. */
  noteUser(user: { id: string; role: Role } | null): void;
  readonly enabled: boolean;
}

/** What is kept while the tracker loads; more than this many early errors are one problem anyway. */
const MAX_PENDING = 20;

export function createErrorReporter(options: Partial<TrackerOptions>, load: LoadTracker): ErrorReporter {
  const dsn = options.dsn?.trim();
  let tracker: Tracker | null = null;
  let started = false;
  let user: { id: string; role: string } | null = null;
  const pending: { error: unknown; where: string | undefined }[] = [];

  const send = (target: Tracker, error: unknown, where: string | undefined) =>
    target.captureException(error, where ? { tags: { where } } : undefined);

  return {
    enabled: Boolean(dsn),
    start() {
      if (!dsn || started) return;
      started = true;
      load({ dsn, release: options.release, environment: options.environment ?? 'production' })
        .then((loaded) => {
          tracker = loaded;
          loaded.setUser(user);
          for (const { error, where } of pending.splice(0)) send(loaded, error, where);
        })
        // The tracker failing to load must never be the application's problem.
        .catch(() => undefined);
    },
    report(error, where) {
      if (!dsn) return;
      if (tracker) send(tracker, error, where);
      else if (pending.length < MAX_PENDING) pending.push({ error, where });
    },
    noteUser(next) {
      user = next ? { id: next.id, role: next.role } : null;
      tracker?.setUser(user);
    },
  };
}

/** The address of a page without what follows `?` or `#`: those may hold what was typed. */
export const withoutQuery = (url: string): string => url.split(/[?#]/, 1)[0] ?? url;

/** Loads Sentry's SDK, in a chunk of its own, and sets it up to send no more than is needed. */
const loadSentry: LoadTracker = async ({ dsn, release, environment }) => {
  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn,
    release,
    environment,
    // The SDK would collect all of these by itself; the application sets the account explicitly.
    dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
    // Errors only: no performance traces, no session replays.
    tracesSampleRate: 0,
    beforeSend(event) {
      if (event.request?.url) event.request.url = withoutQuery(event.request.url);
      delete event.request?.cookies;
      delete event.request?.headers;
      // Clicks and requests before the error help; what was typed does not belong in them.
      for (const crumb of event.breadcrumbs ?? []) {
        if (typeof crumb.data?.url === 'string') crumb.data.url = withoutQuery(crumb.data.url);
        if (crumb.category === 'ui.input') delete crumb.message;
      }
      return event;
    },
  });
  return {
    captureException: (error, context) => void Sentry.captureException(error, context),
    setUser: (user) => Sentry.setUser(user),
  };
};

/** What the installation says to the page at start (/runtime-config.js). */
declare global {
  interface Window {
    __EASYMUSIC__?: { errorReportingDsn?: string };
  }
}

/** The address errors go to: the installation's, else the build's, else none. */
export const errorReportingDsn = (): string | undefined =>
  globalThis.window?.__EASYMUSIC__?.errorReportingDsn || import.meta.env.VITE_ERROR_REPORTING_DSN;

/**
 * The address on the origin of the page. The gateway takes reports at every host, and the
 * content policy lets a page connect to its own origin only: a DSN made for another address
 * (localhost, the base domain while the page is at app.) would be blocked. On a developer's
 * machine the address stays as it is.
 */
export function onThisOrigin(dsn: string | undefined, origin = globalThis.window?.location): string | undefined {
  if (!dsn || !origin || /^(localhost|127\.|\[::1\])/.test(origin.hostname)) return dsn;
  try {
    const url = new URL(dsn);
    return `${origin.protocol}//${url.username}@${origin.host}${url.pathname}`;
  } catch {
    return dsn;
  }
}

/** The application's reporter. Off unless the installation gives it an address. */
export const errorReporter = createErrorReporter(
  {
    dsn: onThisOrigin(errorReportingDsn()),
    release: import.meta.env.VITE_BUILD_ID,
    environment: import.meta.env.MODE,
  },
  loadSentry,
);
